import { z } from "zod";

export * from "./dashboard-http-effect.js";

export const tableColumnTypeSchema = z.enum([
  "text",
  "integer",
  "real",
  "boolean",
  "date",
  "datetime",
  "currency",
  "json",
]);

export type TableColumnType = z.infer<typeof tableColumnTypeSchema>;

export const tableColumnSchema = z.object({
  name: z.string().min(1),
  original_name: z.string().min(1).optional(),
  type: tableColumnTypeSchema,
  required: z.boolean().optional(),
  unique: z.boolean().optional(),
  default: z.union([z.string(), z.number(), z.boolean()]).nullable().optional(),
  sort_order: z.number().int().optional(),
});

export type TableColumn = z.infer<typeof tableColumnSchema>;

export const createTableRequestSchema = z.object({
  name: z.string().min(1),
  template: z.string().min(1).nullable().optional(),
  columns: z.array(tableColumnSchema).min(1),
});

export type CreateTableRequest = z.infer<typeof createTableRequestSchema>;

export const tableSchemaSchema = z
  .object({
    id: z.string().min(1).optional(),
    name: z.string().min(1),
    template: z.string().nullable().optional(),
    columns: z.array(tableColumnSchema),
    created_at: z.string().nullable().optional(),
  })
  .passthrough();

export type TableSchema = z.infer<typeof tableSchemaSchema>;

export const createTableResponseSchema = tableSchemaSchema;

export type CreateTableResponse = z.infer<typeof createTableResponseSchema>;

export const tableRowsResponseSchema = z
  .object({
    columns: z
      .array(
        z.object({
          name: z.string().min(1),
          type: z.string().min(1),
          required: z.boolean().optional(),
        })
      )
      .optional(),
    rows: z.array(z.record(z.string(), z.unknown())),
    total: z.number().int().nonnegative(),
    limit: z.number().int().positive().optional(),
    offset: z.number().int().nonnegative().optional(),
  })
  .passthrough();

export type TableRowsResponse = z.infer<typeof tableRowsResponseSchema>;

export const createAgentRequestSchema = z.object({
  name: z.string().min(1),
  table_id: z.string().min(1).optional(),
  system: z.string().min(1).optional(),
  model: z.string().min(1).optional(),
  output_schema: z.unknown().optional(),
});

export type CreateAgentRequest = z.infer<typeof createAgentRequestSchema>;

export const agentListItemSchema = z
  .object({
    id: z.string().min(1),
    object: z.literal("agent").optional(),
    name: z.string().min(1),
    model: z.string().nullable().optional(),
    system: z.string().nullable().optional(),
    output_schema: z.unknown().nullable().optional(),
    table_id: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    version: z.number().int().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
  })
  .passthrough();

export type AgentListItem = z.infer<typeof agentListItemSchema>;

export const createAgentResponseSchema = agentListItemSchema;

export type CreateAgentResponse = z.infer<typeof createAgentResponseSchema>;

export const listAgentsResponseSchema = z
  .object({
    data: z.array(agentListItemSchema),
    has_more: z.boolean().optional(),
    next_cursor: z.string().nullable().optional(),
  })
  .passthrough();

export type ListAgentsResponse = z.infer<typeof listAgentsResponseSchema>;

export const inboxSchema = z
  .object({
    id: z.string().min(1),
    object: z.literal("inbox").optional(),
    name: z.string().optional(),
    email: z.string().min(1),
    collection_id: z.string().nullable().optional(),
    agent_id: z.string().nullable().optional(),
    table_id: z.string().nullable().optional(),
    prompt: z.string().nullable().optional(),
    allowed_senders: z.array(z.string()).nullable().optional(),
    from_name: z.string().nullable().optional(),
    active: z.boolean(),
    created_at: z.string().optional(),
  })
  .passthrough();

export type Inbox = z.infer<typeof inboxSchema>;

export const createInboxRequestSchema = z.object({
  name: z.string().min(1),
  agent_id: z.string().min(1).optional(),
  table_id: z.string().min(1).optional(),
  prompt: z.string().min(1).optional(),
});

export type CreateInboxRequest = z.infer<typeof createInboxRequestSchema>;

export const inboxListResponseSchema = z
  .object({
    data: z.array(inboxSchema),
    has_more: z.boolean().optional(),
    next_cursor: z.string().nullable().optional(),
  })
  .passthrough();

export type InboxListResponse = z.infer<typeof inboxListResponseSchema>;

export const documentListItemSchema = z
  .object({
    id: z.string().min(1),
    file_name: z.string().nullable().optional(),
    phase: z.string().optional(),
    status: z.string().optional(),
    created_at: z.string().optional(),
    inserted_at: z.string().optional(),
    updated_at: z.string().optional(),
    total_pages: z.number().int().nonnegative().optional(),
    pages_completed: z.number().int().nonnegative().optional(),
    thumbnail_url: z.string().optional(),
    is_public: z.boolean().optional(),
    source: z.string().optional(),
  })
  .passthrough();

export type DocumentListItem = z.infer<typeof documentListItemSchema>;

export const documentListResponseSchema = z
  .object({
    data: z.array(documentListItemSchema),
    has_more: z.boolean().optional(),
    next_cursor: z.string().nullable().optional(),
    documents: z.array(documentListItemSchema).optional(),
  })
  .passthrough();

export type DocumentListResponse = z.infer<typeof documentListResponseSchema>;

export const collectionListItemSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    slug: z.string().optional(),
    document_count: z.number().int().nonnegative().optional(),
  })
  .passthrough();

export type CollectionListItem = z.infer<typeof collectionListItemSchema>;

export const collectionListResponseSchema = z.union([
  z.array(collectionListItemSchema),
  z
    .object({
      data: z.array(collectionListItemSchema),
    })
    .passthrough(),
  z
    .object({
      collections: z.array(collectionListItemSchema),
    })
    .passthrough(),
]);

export type CollectionListResponse = z.infer<typeof collectionListResponseSchema>;

export const collectionDetailSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    slug: z.string().optional(),
    documents: z
      .array(
        z
          .object({
            id: z.string().min(1),
            name: z.string().min(1),
          })
          .passthrough()
      )
      .optional(),
  })
  .passthrough();

export type CollectionDetail = z.infer<typeof collectionDetailSchema>;

// ── Table Registry (D1) ─────────────────────────────────────────────────────

export const tableListItemSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    template: z.string().nullable().optional(),
    agent_id: z.string().nullable().optional(),
    created_at: z.string().optional(),
  })
  .passthrough();

export type TableListItem = z.infer<typeof tableListItemSchema>;

export const listTablesResponseSchema = z
  .object({
    data: z.array(tableListItemSchema),
    has_more: z.boolean().optional(),
    next_cursor: z.string().nullable().optional(),
  })
  .passthrough();

export type ListTablesResponse = z.infer<typeof listTablesResponseSchema>;

// -- Workflow Runs -----------------------------------------------------------

export const workflowQualityPresetSchema = z.enum(["fast", "balanced", "high"]);

export type WorkflowQualityPreset = z.infer<typeof workflowQualityPresetSchema>;

export const workflowFileInputSchema = z.object({
  file_id: z.string().min(1),
});

export type WorkflowFileInput = z.infer<typeof workflowFileInputSchema>;

export const invoiceExtractionRunRequestSchema = z
  .object({
    inputs: z.array(workflowFileInputSchema).min(1),
    table_id: z.string().min(1).optional(),
    quality: workflowQualityPresetSchema.default("balanced"),
  })
  .passthrough();

export type InvoiceExtractionRunRequest = z.infer<
  typeof invoiceExtractionRunRequestSchema
>;

export const invoiceExtractionRunResponseSchema = z
  .object({
    id: z.string().min(1),
    run_id: z.string().min(1),
    object: z.literal("run"),
    status: z.string().min(1),
    workflow_name: z.literal("invoice-extraction"),
    workflow_label: z.string().min(1),
    workflow_version: z.number().int().positive(),
    quality: workflowQualityPresetSchema,
    inputs: z.array(workflowFileInputSchema),
    table_id: z.string().min(1).nullable(),
    agent_id: z.string().min(1).nullable().optional(),
    // Compat URL fields: still emitted by the session-backed GET handlers, but
    // the deprecated POST handler dropped them (#1389) because they pointed at
    // the removed /v1/invoices routes (#1368).
    events_url: z.string().min(1).optional(),
    stream_url: z.string().min(1).optional(),
    rows_url: z.string().min(1).optional(),
    exports: z
      .object({
        csv_url: z.string().min(1),
        json_url: z.string().min(1),
        xml_url: z.string().min(1).optional(),
        xlsx_url: z.string().min(1).optional(),
      })
      .passthrough()
      .optional(),
    created_at: z.string().min(1),
  })
  .passthrough();

export type InvoiceExtractionRunResponse = z.infer<
  typeof invoiceExtractionRunResponseSchema
>;

export const invoiceExtractionRunListResponseSchema = z
  .object({
    object: z.literal("list"),
    data: z.array(invoiceExtractionRunResponseSchema),
    has_more: z.boolean(),
    next_cursor: z.string().nullable(),
  })
  .passthrough();

export type InvoiceExtractionRunListResponse = z.infer<
  typeof invoiceExtractionRunListResponseSchema
>;

export const invoiceExtractionExceptionSchema = z
  .object({
    id: z.string().min(1),
    object: z.literal("invoice_exception"),
    run_id: z.string().min(1),
    table_id: z.string().min(1),
    row_id: z.string().min(1).nullable().optional(),
    file_id: z.string().min(1).nullable().optional(),
    field: z.string().min(1).nullable().optional(),
    code: z.enum([
      "missing_required_field",
      "document_parse_failed",
      "empty_result",
      "run_failed",
      "validation_warning",
    ]),
    severity: z.enum(["warning", "error"]),
    status: z.enum(["open", "resolved"]),
    message: z.string().min(1),
    value: z.unknown().optional(),
    source_page: z.number().int().positive().nullable().optional(),
    source_bbox: z.string().min(1).nullable().optional(),
    created_at: z.string().min(1),
  })
  .passthrough();

export type InvoiceExtractionException = z.infer<
  typeof invoiceExtractionExceptionSchema
>;

export const invoiceExtractionExceptionListResponseSchema = z
  .object({
    object: z.literal("list"),
    data: z.array(invoiceExtractionExceptionSchema),
    has_more: z.boolean().optional(),
    next_cursor: z.string().nullable().optional(),
  })
  .passthrough();

export type InvoiceExtractionExceptionListResponse = z.infer<
  typeof invoiceExtractionExceptionListResponseSchema
>;
